// src/components/CustomMessageModal.js - Custom Message Modal with Media Upload
import React, { useState, useEffect } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MessageIcon from '@mui/icons-material/Message';
import { customMessageService, groupService } from '../services/api';
import { useToast } from './Toast';

const CustomMessageModal = ({ isOpen, onClose, event, guests = [], prefilledRecipientFilter = 'all', preSelectedGuests = null }) => {
  const toast = useToast();

  const [messageData, setMessageData] = useState({
    message: '',
    recipientFilter: prefilledRecipientFilter, // 'all', 'confirmed', 'confirmed_maybe', 'group:groupId'
    scheduleOption: 'immediate' // 'immediate', 'scheduled'
  });

  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Schedule send state
  const [sendMode, setSendMode] = useState('immediate'); // 'immediate' or 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // Load groups when modal opens
  useEffect(() => {
    if (isOpen && event) {
      loadGroups();
    }
  }, [isOpen, event]);

  // Update recipientFilter when prefilled value changes
  useEffect(() => {
    if (prefilledRecipientFilter) {
      setMessageData(prev => ({
        ...prev,
        recipientFilter: prefilledRecipientFilter
      }));
    }
  }, [prefilledRecipientFilter]);

  const loadGroups = async () => {
    if (!event) return;

    setLoadingGroups(true);
    try {
      const eventId = event._id || event.id;
      const groupsData = await groupService.getGroups(eventId);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  if (!isOpen) return null;

  // When pre-selected guests are provided, use them directly (skip filter dropdown)
  const isPreSelected = Array.isArray(preSelectedGuests) && preSelectedGuests.length > 0;

  // Filter guests based on selected criteria
  const getFilteredGuests = () => {
    if (isPreSelected) {
      return preSelectedGuests.filter(g => g.whatsappNumber && g.whatsappNumber.trim());
    }

    const validGuests = guests.filter(guest => guest.whatsappNumber && guest.whatsappNumber.trim());

    // Check if it's a group filter (format: "group:groupId")
    if (messageData.recipientFilter.startsWith('group:')) {
      const groupId = messageData.recipientFilter.split(':')[1];
      // P0 BUG FIX: Group membership is stored in Guest.groups[], not GroupDefinition.guestIds
      const guestsInGroup = validGuests.filter(guest =>
        guest.groups && guest.groups.some(g =>
          (g.groupId?.toString() === groupId?.toString()) ||
          (g.groupId?._id?.toString() === groupId?.toString())
        )
      );
      return guestsInGroup;
    }

    switch (messageData.recipientFilter) {
      case 'confirmed':
        return validGuests.filter(guest => guest.rsvp === 'yes');
      case 'pending':
        return validGuests.filter(guest => guest.rsvp === 'pending');
      case 'declined':
        return validGuests.filter(guest => guest.rsvp === 'no');
      case 'notinvited':
        return validGuests.filter(guest => guest.rsvp === 'unassigned');
      case 'all':
      default:
        return validGuests;
    }
  };

  const filteredGuests = getFilteredGuests();

  // Get user's timezone
  const getUserTimezone = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();
    const tzAbbr = now.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ')[2];
    return { timezone, abbr: tzAbbr };
  };

  const { timezone, abbr: timezoneAbbr } = getUserTimezone();

  // Validate and get scheduled datetime
  const getScheduledDateTime = () => {
    if (!scheduledDate || !scheduledTime) {
      toast.error('Please select both date and time for scheduled send');
      return null;
    }

    const dateTime = new Date(`${scheduledDate}T${scheduledTime}`);
    const now = new Date();

    // Check if in the past
    if (dateTime < now) {
      toast.error('Cannot schedule for past time');
      return null;
    }

    // Check if at least 5 minutes in future
    const minFutureTime = new Date(now.getTime() + 5 * 60000);
    if (dateTime < minFutureTime) {
      toast.error('Please schedule at least 5 minutes in the future');
      return null;
    }

    return dateTime;
  };

  // Format scheduled datetime for display
  const formatScheduledDateTime = (dateTime) => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    };
    return dateTime.toLocaleString('en-US', options);
  };

  // Handle file selection and preview
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset previous errors
    setUploadError('');

    // Check file size before preview
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    const maxSizes = {
      'image': 5,
      'video': 16,
      'document': 100
    };

    let mediaType = 'document';
    if (file.type.startsWith('image/')) mediaType = 'image';
    else if (file.type.startsWith('video/')) mediaType = 'video';

    if (file.size > maxSizes[mediaType] * 1024 * 1024) {
      setUploadError(`File too large. ${mediaType} files must be under ${maxSizes[mediaType]}MB (current: ${fileSizeMB}MB)`);
      return;
    }

    setMediaFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setMediaPreview({
          type: 'image',
          url: e.target.result,
          name: file.name,
          size: fileSizeMB
        });
      };
      reader.readAsDataURL(file);
    } else {
      setMediaPreview({
        type: file.type.startsWith('video/') ? 'video' : 'document',
        name: file.name,
        size: fileSizeMB
      });
    }
  };

  // Remove selected media
  const handleRemoveMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setUploadError('');
  };

  // Upload media file
  const uploadMediaFile = async () => {
    if (!mediaFile) return null;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('media', mediaFile);

      const response = await customMessageService.uploadMedia(formData);
      return response.mediaUrl;
    } catch (error) {
      console.error('Media upload failed:', error);
      setUploadError(error.response?.data?.error || 'Upload failed');
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // Send custom message
  const handleSendMessage = async () => {
    if (!messageData.message.trim() && !mediaFile) {
      toast.warning('Please enter a message or select media to send.');
      return;
    }

    if (filteredGuests.length === 0) {
      toast.warning('No guests match the selected criteria.');
      return;
    }

    setSending(true);
    try {
      let mediaUrl = null;
      if (mediaFile) {
        mediaUrl = await uploadMediaFile();
      }

      // Prepare payload
      const eventId = event._id || event.id;
      const customMessageData = {
        message: messageData.message,
        mediaUrl: mediaUrl,
        recipients: filteredGuests.map(guest => ({
          guestId: guest._id || guest.id,
          whatsappNumber: guest.whatsappNumber,
          name: guest.name
        })),
        recipientFilter: messageData.recipientFilter
      };

      // Add scheduledFor if in scheduled mode
      if (sendMode === 'scheduled') {
        const scheduledDateTime = getScheduledDateTime();
        if (!scheduledDateTime) {
          setSending(false);
          return; // Validation failed
        }
        customMessageData.scheduledFor = scheduledDateTime.toISOString();
      }

      const response = await customMessageService.sendCustomMessage(eventId, customMessageData);

      // Handle response with different status counts
      const { instantDelivery, queuedForReply, scheduled, scheduledFor } = response.data || {};

      // Enhanced toast messages based on send mode and results
      if (sendMode === 'scheduled' && scheduled) {
        const formattedDate = formatScheduledDateTime(new Date(scheduledFor));
        toast.success(`Message scheduled successfully!\nWill be sent ${formattedDate} to ${filteredGuests.length} guests`);
      } else if (queuedForReply > 0) {
        toast.success(`Message queued successfully!\n• ${instantDelivery || 0} delivered instantly\n• ${queuedForReply} will receive when they respond`);
      } else {
        toast.success(`Message sent successfully!\nDelivered instantly to ${instantDelivery || filteredGuests.length} guests`);
      }

      onClose();

      // Reset form
      setMessageData({
        message: '',
        recipientFilter: 'all',
        scheduleOption: 'immediate'
      });
      setMediaFile(null);
      setMediaPreview(null);
      setSendMode('immediate');
      setScheduledDate('');
      setScheduledTime('');

    } catch (error) {
      console.error('Failed to send custom message:', error);
      toast.error(error.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="media-icon" />;
      case 'video': return <VideoFileIcon className="media-icon" />;
      default: return <PictureAsPdfIcon className="media-icon" />;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content custom-message-modal">
        <div className="modal-header">
          <h3>Send Custom Message</h3>
          <p className="event-name">Event: {event?.name}</p>
        </div>
        
        <div className="modal-body">
          {/* Message Composer */}
          <div className="form-section">
            <label className="form-label">Message</label>
            <textarea
              value={messageData.message}
              onChange={(e) => {
                if (e.target.value.length <= 1600) {
                  setMessageData({ ...messageData, message: e.target.value });
                }
              }}
              placeholder="Enter your custom message here..."
              className="message-textarea"
              rows={4}
              maxLength={1600}
            />
            <div style={{
              display: 'flex', justifyContent: 'flex-end', marginTop: 4, fontSize: 12,
              color: messageData.message.length > 1500 ? (messageData.message.length > 1580 ? '#d32f2f' : '#b58a37') : '#888'
            }}>
              {messageData.message.length} / 1,600
            </div>
          </div>

          {/* Media Upload */}
          <div className="form-section">
            <label className="form-label">
              <AttachFileIcon className="form-icon" />
              Media Attachment (Optional)
            </label>
            
            {!mediaPreview && (
              <div className="media-upload-area">
                <input
                  type="file"
                  id="media-upload"
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                <label htmlFor="media-upload" className="upload-button">
                  <AttachFileIcon />
                  Choose File
                </label>
                <div className="file-limits">
                  <small>
                    📸 Images: 5MB max • 🎥 Videos: 16MB max • 📄 Documents: 100MB max
                  </small>
                </div>
              </div>
            )}

            {/* Media Preview */}
            {mediaPreview && (
              <div className="media-preview">
                <div className="media-info">
                  {getMediaIcon(mediaPreview.type)}
                  <div className="media-details">
                    <div className="media-name">{mediaPreview.name}</div>
                    <div className="media-size">{mediaPreview.size}MB</div>
                  </div>
                  <button 
                    onClick={handleRemoveMedia}
                    className="remove-media-btn"
                  >
                    <DeleteIcon />
                  </button>
                </div>
                
                {mediaPreview.type === 'image' && (
                  <div className="image-preview">
                    <img src={mediaPreview.url} alt="Preview" />
                  </div>
                )}
              </div>
            )}

            {uploadError && (
              <div className="upload-error">
                {uploadError}
              </div>
            )}
          </div>

          {/* Recipient Selection */}
          <div className="form-section">
            <label className="form-label">Recipients</label>
            {isPreSelected ? (
              <>
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px',
                  background: 'var(--c-muted, #f5f5f5)', borderRadius: 8, border: '1px solid var(--c-border, #e0e0e0)',
                  maxHeight: 120, overflowY: 'auto'
                }}>
                  {preSelectedGuests.map(g => (
                    <span key={g._id || g.guestId || g.name} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 500,
                      background: g.whatsappNumber ? 'var(--c-primary, #1e514b)' : '#ccc',
                      color: '#fff'
                    }}>
                      {g.name}
                      {!g.whatsappNumber && <span title="No WhatsApp number"> (no phone)</span>}
                    </span>
                  ))}
                </div>
                <div className="recipient-count" style={{ marginTop: 6 }}>
                  Will send to {filteredGuests.length} of {preSelectedGuests.length} selected guests (with WhatsApp numbers)
                </div>
              </>
            ) : (
              <>
                <select
                  value={messageData.recipientFilter}
                  onChange={(e) => setMessageData({ ...messageData, recipientFilter: e.target.value })}
                  className="recipient-selector"
                >
                  <option value="all">All Guests ({guests.filter(g => g.whatsappNumber).length})</option>
                  <option value="confirmed">Confirmed ({guests.filter(g => g.whatsappNumber && g.rsvp === 'yes').length})</option>
                  <option value="pending">Pending ({guests.filter(g => g.whatsappNumber && g.rsvp === 'pending').length})</option>
                  <option value="declined">Declined ({guests.filter(g => g.whatsappNumber && g.rsvp === 'no').length})</option>
                  <option value="notinvited">Not Invited ({guests.filter(g => g.whatsappNumber && g.rsvp === 'unassigned').length})</option>

                  {/* Groups Section */}
                  {groups.length > 0 && (
                    <optgroup label="━━━━━━ Groups ━━━━━━">
                      {groups.map(group => {
                        // P0 BUG FIX: Count guests using Guest.groups[] membership
                        const groupGuestsWithPhone = guests.filter(g =>
                          g.whatsappNumber &&
                          g.groups && g.groups.some(memberGroup =>
                            (memberGroup.groupId?.toString() === group._id?.toString()) ||
                            (memberGroup.groupId?._id?.toString() === group._id?.toString())
                          )
                        );
                        return (
                          <option key={group._id} value={`group:${group._id}`}>
                            {group.name} ({groupGuestsWithPhone.length} with WhatsApp)
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                </select>

                <div className="recipient-count">
                  Will send to {filteredGuests.length} guests with WhatsApp numbers
                </div>
              </>
            )}
          </div>

          {/* Schedule Send Section */}
          <div className="form-section">
            <label className="form-label">Schedule</label>

            {/* Radio Toggle */}
            <div className="schedule-toggle">
              <label className="schedule-option">
                <input
                  type="radio"
                  name="sendMode"
                  value="immediate"
                  checked={sendMode === 'immediate'}
                  onChange={(e) => setSendMode(e.target.value)}
                />
                <span>Send immediately</span>
              </label>
              <label className="schedule-option">
                <input
                  type="radio"
                  name="sendMode"
                  value="scheduled"
                  checked={sendMode === 'scheduled'}
                  onChange={(e) => setSendMode(e.target.value)}
                />
                <span>Schedule for later</span>
              </label>
            </div>

            {/* Date/Time Pickers - Show only in scheduled mode */}
            {sendMode === 'scheduled' && (
              <div className="schedule-section">
                <div className="datetime-row">
                  <div className="datetime-field">
                    <label className="datetime-label">
                      <CalendarTodayIcon className="datetime-icon" />
                      Date
                    </label>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className="datetime-input"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="datetime-field">
                    <label className="datetime-label">
                      <AccessTimeIcon className="datetime-icon" />
                      Time
                    </label>
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="datetime-input"
                    />
                  </div>
                </div>

                {/* Timezone Info */}
                <div className="timezone-info">
                  <span className="timezone-label">Timezone:</span>
                  <span className="timezone-value">{timezone} ({timezoneAbbr})</span>
                </div>

                {/* Schedule Preview */}
                {scheduledDate && scheduledTime && (
                  <div className="schedule-preview">
                    <MessageIcon className="schedule-preview-icon" />
                    <span>Recipients will receive this on {formatScheduledDateTime(new Date(`${scheduledDate}T${scheduledTime}`))}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="modal-footer-left">
            {/* Preview info */}
          </div>
          <div className="modal-footer-right">
            <button 
              className="cancel-btn"
              onClick={onClose}
              disabled={sending || uploading}
            >
              Cancel
            </button>
            <button 
              className="send-btn"
              onClick={handleSendMessage}
              disabled={sending || uploading || (!messageData.message.trim() && !mediaFile)}
            >
              {uploading ? (
                <>🔄 Uploading...</>
              ) : sending ? (
                <>🔄 Sending...</>
              ) : (
                <><SendIcon className="btn-icon" /> Send Message</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomMessageModal;